/**
 * Dev-only demo-account switcher — the web port of the native apps' Dev Tools ▸ Demo accounts,
 * reading the SAME committed list (`native-apps/demo-accounts.json`) so both platforms hop between
 * the same accounts.
 *
 * **Where the switch actually happens: the dev proxy, not the browser.** On localhost the browser
 * holds no Wix session — `dev-proxy.mjs` is what attaches one to every upstream call (it mints a
 * `wixAT` from the `WIX_SESSION_DEV` in `.env.local`). So switching accounts means telling the proxy
 * to attach a DIFFERENT session: it signs in as the demo account exactly like the native app does
 * (`users.wix.com/oneapp/auth/sign-in`; the demo accounts have 2FA off, so it is silent), keeps that
 * `wixSession2` in memory, and re-harvests. Returning drops the override and you are back on your own
 * session with no re-login.
 *
 * That bounds the SWITCH to dev: the control plane (`/__dev/demo-accounts*`) is served by the dev
 * sidecar, which only runs under `next dev`, and reached through a dev-only rewrite in next.config.js.
 * A deployed pod has no such route — hence `unavailable` below, which is what the panel shows there
 * (and when the proxy simply isn't running). The menu still OFFERS the item wherever the dev tools
 * are unlocked: saying "not mounted on this origin" beats silently hiding it.
 */
import { devToolsUnlocked } from './dev-tools';

/** One preset account. The password never leaves the proxy — the client switches by email. */
export interface DemoAccount {
  label: string;
  email: string;
}

export interface DemoAccountsState {
  accounts: DemoAccount[];
  /** The demo account currently being impersonated, or null when you're on your own session. */
  active: DemoAccount | null;
  /** Who "Return to …" goes back to — your own account's name, when the proxy could read it. */
  originLabel: string | null;
}

export type DemoAccountsResult =
  | { status: 'ready'; state: DemoAccountsState }
  /** The dev proxy isn't answering — not running, or this isn't a `next dev` origin. */
  | { status: 'unavailable'; message: string }
  | { status: 'error'; message: string };

const BASE = '/__dev/demo-accounts';

async function call(path: string, init?: RequestInit): Promise<DemoAccountsResult> {
  if (!devToolsUnlocked()) return { status: 'unavailable', message: 'Dev tools are not available here.' };
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    return {
      status: 'unavailable',
      message: `Can't reach the dev proxy — is \`yarn dev\` running? (${err instanceof Error ? err.message : String(err)})`,
    };
  }
  // A deployed pod (or a dev server with no proxy behind the rewrite) answers the catch-all page with
  // HTML instead of JSON — read that as "no switcher here" rather than a parse crash.
  if (res.status === 404 || !res.headers.get('content-type')?.includes('application/json')) {
    return { status: 'unavailable', message: 'The dev proxy control plane is not mounted on this origin.' };
  }
  const body = (await res.json()) as Partial<DemoAccountsState> & { message?: string; error?: string };
  if (!res.ok) return { status: 'error', message: body.message || body.error || `HTTP ${res.status}` };
  return {
    status: 'ready',
    state: { accounts: body.accounts ?? [], active: body.active ?? null, originLabel: body.originLabel ?? null },
  };
}

/** The account list + who is active right now. */
export function fetchDemoAccounts(): Promise<DemoAccountsResult> {
  return call(BASE);
}

/**
 * Impersonate `email` from here on. The proxy signs in and re-harvests before answering, so by the
 * time this resolves the next page load is already on the new account.
 */
export function switchToDemoAccount(email: string): Promise<DemoAccountsResult> {
  return call(`${BASE}/switch`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/** Back to the session in `.env.local` — your own dev login. */
export function returnToOwnAccount(): Promise<DemoAccountsResult> {
  return call(`${BASE}/return`, { method: 'POST' });
}
